import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { Textarea, Button } from '../ui';

export default function HomeworkItemCard({ item, onRemove, onUpdateInstructions }) {
  const { t } = useTranslation();
  const [editingInstructions, setEditingInstructions] = useState(false);
  const [instructions, setInstructions] = useState(item.instructions || '');

  const isActivity = item.sourceable_type === 'Activity';

  const handleSaveInstructions = () => {
    onUpdateInstructions?.(item.id, instructions);
    setEditingInstructions(false);
  };

  return (
    <div className={cn(
      'flex items-start gap-3 p-3 rounded-lg',
      'border border-gray-200 dark:border-gray-700',
      'bg-white dark:bg-gray-900',
      'group'
    )}>
      {/* Drag handle */}
      <div className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-300 dark:text-gray-600 mt-0.5">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      </div>

      <div className="flex-1 min-w-0">
        {/* Type and title */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
            {isActivity ? 'Activity' : 'Question'}
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {item.title || item.prompt?.substring(0, 60) || t('homework.untitled')}
          </span>
        </div>

        {/* Source info */}
        <div className="flex items-center gap-2 text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
          {item.source_lesson_title && (
            <span>{t('homework.fromLesson', { title: item.source_lesson_title })}</span>
          )}
          {isActivity && item.question_count != null && (
            <span>{t('homework.questionsCount', { count: item.question_count })}</span>
          )}
          {item.stimulus_title && (
            <span>{t('homework.stimulusLabel', { title: item.stimulus_title })}</span>
          )}
        </div>

        {/* Instructions */}
        {editingInstructions ? (
          <div className="mt-2 space-y-2">
            <Textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={2}
              placeholder={t('homework.instructionsPlaceholder')}
              className="text-xs"
            />
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditingInstructions(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" size="sm" onClick={handleSaveInstructions}>{t('common.save')}</Button>
            </div>
          </div>
        ) : item.instructions ? (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
            {item.instructions}
          </p>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => setEditingInstructions(!editingInstructions)}
          className="p-1 rounded text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:hover:text-white dark:hover:bg-gray-700 transition-colors"
          title={t('homework.editInstructions')}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          onClick={() => {
            if (confirm(t('homework.removeConfirm'))) {
              onRemove?.(item.id);
            }
          }}
          className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          title={t('homework.removeFromHomework')}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
